import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../libs/axios';
import {
  FiInfo,
  FiMapPin,
  FiUsers,
  FiCalendar,
  FiUser,
  FiMail,
  FiPhone,
  FiEdit3,
  FiAlertCircle,
  FiFileText,
  FiBriefcase
} from 'react-icons/fi';

// Types
interface StateCircle {
  id: number;
  state_name: string;
  circle_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  suspended_at: string | null;
  suspended_by: number | null;
}

interface Representative {
  id: number;
  client_id: number;
  contact_person: string;
  contact_number: string;
  designation_id: number;
  representative_location: string;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Client {
  id: number;
  client_full_name: string;
  client_short_name: string;
  address: string;
  gst_number: string;
  state_circle_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  suspended_at: string | null;
  suspended_by: number | null;
  representatives: Representative[];
  state_circle: StateCircle;
  client_projects: any[];
}

interface ClientData {
  client: Client;
}

interface TabProps {
  data: ClientData;
}

// Client Overview Tab Component
const ClientOverviewTab: React.FC<TabProps> = ({ data }) => {
  const { client } = data;

  const getStatusColor = (isActive: boolean): string => {
    return isActive ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
  };

  return (
    <div className="space-y-4">
      {/* Basic Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Client Information</h3>
          <button className="flex items-center gap-1 px-3 py-1 text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors text-sm">
            <FiEdit3 size={14} />
            Edit
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiUser className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{client.client_full_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Short Name</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{client.client_short_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">GST Number</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{client.gst_number}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">State & Circle</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiMapPin className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{client.state_circle.state_name} - {client.state_circle.circle_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <div className={`w-2 h-2 rounded-full ${client.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(client.is_active)}`}>
                  {client.is_active ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Created Date</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiCalendar className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{new Date(client.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
          <div className="flex items-start gap-2 p-2 bg-gray-50 rounded">
            <FiMapPin className="text-gray-400 mt-0.5" size={14} />
            <span className="text-sm text-gray-900">{client.address}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Representatives Tab Component
const RepresentativesTab: React.FC<TabProps> = ({ data }) => {
  const { client } = data;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Client Representatives</h3>

        {client.representatives.length === 0 ? (
          <div className="p-8 text-center">
            <div className="p-3 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <FiUsers className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-900 mb-1">No representatives added</h3>
            <p className="text-sm text-gray-600 mb-4">Representatives will appear here once they are added.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {client.representatives.map((rep) => (
              <div key={rep.id} className="border border-gray-200 rounded p-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-100 text-blue-700">
                    <FiUser size={16} />
                  </div>

                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">
                      {rep.contact_person}
                    </h4>

                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <FiMail size={12} />
                        {rep.email}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <FiPhone size={12} />
                        {rep.contact_number}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <FiMapPin size={12} />
                        {rep.representative_location}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rep.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {rep.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                    <FiMail size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Projects Tab Component
const ProjectsTab: React.FC<TabProps> = ({ data }) => {
  const { client } = data;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Client Projects</h3>

        {client.client_projects.length === 0 ? (
          <div className="p-8 text-center">
            <div className="p-3 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <FiBriefcase className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-900 mb-1">No projects found</h3>
            <p className="text-sm text-gray-600 mb-4">Projects associated with this client will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {client.client_projects.map((project: any) => (
              <div key={project.id} className="p-4 hover:bg-gray-50 transition-colors duration-200">
                <h4 className="text-base font-semibold text-gray-900 mb-1">{project.project_name}</h4>
                <p className="text-sm text-gray-600">{project.project_description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main ClientView Component
export default function ClientView() {
  const { id } = useParams<{ id: string }>();
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiInfo },
    { id: 'representatives', label: 'Representatives', icon: FiUsers },
    // { id: 'projects', label: 'Projects', icon: FiBriefcase },
  ];

  useEffect(() => {
    const fetchClient = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/clients/${id}`);
        setClientData(response.data.data);
      } catch (error) {
        console.error('Error fetching client data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchClient();
  }, [id]);

  const renderTabContent = () => {
    if (!clientData) return null;

    switch (activeTab) {
      case 'overview':
        return <ClientOverviewTab data={clientData} />;
      case 'representatives':
        return <RepresentativesTab data={clientData} />;
      // case 'projects':
      //   return <ProjectsTab data={clientData} />;
      default:
        return <ClientOverviewTab data={clientData} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading client details...</p>
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Client Not Found</h2>
          <p className="text-gray-600">The client you're looking for doesn't exist or you don't have permission to view it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Client Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded flex items-center justify-center text-white text-sm font-bold">
              {clientData.client.client_full_name.substring(0, 2).toUpperCase()}
            </div>

            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">
                {clientData.client.client_full_name}
              </h1>
              <p className="text-sm text-gray-600 mb-1">{clientData.client.client_short_name}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <FiFileText size={12} />
                  {clientData.client.gst_number}
                </div>
                <div className="flex items-center gap-1">
                  <FiMapPin size={12} />
                  {clientData.client.state_circle.state_name}
                </div>
                <div className="flex items-center gap-1">
                  <FiCalendar size={12} />
                  Created {new Date(clientData.client.created_at).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${clientData.client.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  {clientData.client.is_active ? 'ACTIVE' : 'SUSPENDED'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
}